import { useEffect, useRef } from 'react';
import {
  getPostHogSessionId,
  useTrackEvent,
} from 'shared/utils/useTrackEvent';

type ActivityPulseOptions = {
  eventName: Parameters<ReturnType<typeof useTrackEvent>>[0];
  payload: Record<string, unknown>;
  intervalSeconds?: number;
  activeWindowMs?: number;
  enabled?: boolean;
};

export const useActivityPulse = ({
  eventName,
  payload,
  intervalSeconds = 60,
  activeWindowMs = 60_000,
  enabled = true,
}: ActivityPulseOptions) => {
  const trackEvent = useTrackEvent();
  const lastActivityRef = useRef<number>(Date.now());
  const hasActivitySinceEnableRef = useRef<boolean>(false);
  // Timestamp of the previous heartbeat fire, used to compute the real
  // elapsed time between pulses instead of assuming the interval length.
  const lastPulseRef = useRef<number>(Date.now());

  // Keep the latest payload in a ref so the interval always reads fresh
  // values without needing to tear down and recreate the timer.
  const payloadRef = useRef(payload);
  payloadRef.current = payload;

  useEffect(() => {
    if (!enabled) return;
    const now = Date.now();
    lastActivityRef.current = now;
    lastPulseRef.current = now;
    hasActivitySinceEnableRef.current = false;

    const markActive = () => {
      lastActivityRef.current = Date.now();
      hasActivitySinceEnableRef.current = true;
    };

    document.addEventListener('keydown', markActive);
    document.addEventListener('click', markActive);
    document.addEventListener('scroll', markActive, { passive: true });
    // document.addEventListener('mousemove', markActive, { passive: true });

    const intervalId = window.setInterval(() => {
      const tick = Date.now();
      const idleMs = tick - lastActivityRef.current;
      if (hasActivitySinceEnableRef.current && idleMs < activeWindowMs) {
        // Real wall-clock time since the last heartbeat. We intentionally do
        // NOT subtract idle time here — `idleMs` is sent alongside so the
        // active/idle split can be decided at query time without changing the
        // meaning of this field the day the subtraction would ship.
        const elapsedActiveMs = tick - lastPulseRef.current;
        trackEvent(eventName, {
          ...payloadRef.current,
          activeMs: elapsedActiveMs,
          idleMs,
          visibilityState: document.visibilityState,
          sessionId: getPostHogSessionId(),
        });
      }
      lastPulseRef.current = tick;
    }, intervalSeconds * 1000);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('keydown', markActive);
      document.removeEventListener('click', markActive);
      document.removeEventListener('scroll', markActive);
      // document.removeEventListener('mousemove', markActive);
    };
  }, [activeWindowMs, enabled, eventName, intervalSeconds, trackEvent]);
};
